import * as React from 'react';

import XUIButton from '@xero/xui/react/button';
import { XUICompositionSplit } from '@xero/xui/react/compositions';
import XUIIllustration from '@xero/xui/react/illustration';

import { errorImageUrl } from '../../static/constants/errorImages';
import { componentClassName } from '../../helpers/string_helpers';

import './ErrorComponent.scss';

interface IErrorButtonProps {
    buttonType: React.ComponentProps<typeof XUIButton>['variant'];
    href?: string;
    text: string;
    onClick?: () => void;
}

const ErrorButton = ({ buttonType, text, ...rest }: IErrorButtonProps) => (
    <XUIButton
        className='xui-margin-right-small xui-margin-bottom-small'
        fullWidth='small-down'
        isLink={!!rest.href}
        size='small'
        variant={buttonType}
        {...rest}
    >
        {text}
    </XUIButton>
);

interface IButton {
    /**
     * The URL to link to if the button is a link.
     */
    href?: string;

    /**
     * The text to show in the button.
     */
    text: string;

    /**
     * The function to execute when the button is clicked.
     */
    onClick?: () => void;
}

interface IProps {
    /**
     * The page heading.
     */
    heading: React.ReactNode;

    /**
     * The description of your error page.
     */
    description: React.ReactNode;

    /**
     * Use this prop to pass in the primary button's text and onClick functionality.
     */
    primaryButton?: IButton;

    /**
     * Use this prop to pass in your secondary button's text and onClick functionality.
     */
    secondaryButton?: IButton;
}

const createClass = componentClassName('ErrorComponent', 'sbt');

const ErrorComponent = ({ description, heading, primaryButton, secondaryButton }: IProps) => {
    const primaryContent = React.useMemo(
        () => (
            <article className={createClass()}>
                <div>
                    <h2 className={createClass('heading')}>{heading}</h2>
                    <div className={createClass('description')}>{description}</div>
                    {primaryButton && <ErrorButton buttonType='main' {...primaryButton} />}
                    {secondaryButton && <ErrorButton buttonType='standard' {...secondaryButton} />}
                </div>
            </article>
        ),
        [heading, description, primaryButton, secondaryButton]
    );

    const secondaryContent = (
        <XUIIllustration className={createClass('illustration')} size='small' src={errorImageUrl} />
    );

    return (
        <XUICompositionSplit
            className='xui-padding-large'
            primary={primaryContent}
            secondary={secondaryContent}
            hasPrimaryBelowAtSmall
        />
    );
};

ErrorComponent.defaultProps = {
    customImgUrl: null,
    primaryButton: null,
    secondaryButton: null,
};

export default ErrorComponent;
